import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["billId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { requestService } from 'src/core/services/api';
import { API_SERVICE_NAMES } from 'src/constants/apiServiceName';
import { billAutomationBillsRoutes, billingDashboardBaseUrl } from './routes';

/** All three parts are required: the answer is one bill, and any part missing changes which. */

/**
 * Dashboard bulk upload: up to ten files, each landing as its own bill. `propertyIds` may be omitted
 * (every property is resolved from the bill), hold one id for the whole batch, or hold exactly one id
 * per file, paired by position. `propertyAssignments` is the same pairing for a bill covering several
 * properties: one entry per file, each holding that file's property ids, and it wins over `propertyIds`.
 */

/**
 * The optional body of a push. Both fields are corrections a person made to what OCR read, and the
 * service holds each to its own rules — an invoice number it cannot use is refused rather than dropped.
 */

/** Outcome of one file in a batch: `billId` is null when it was a duplicate or failed to land. */

/**
 * A bill's AP push state as the dashboard list carries it. One document is one invoice and one payable,
 * so this is a single verdict rather than a count of parts done.
 */

/** One row of the billing dashboard list. Dates are date-only strings (YYYY-MM-DD). */
/**
 * One utility a bill charges for. `utilityId` and `propertyId` are what the services resolved when
 * they validated the bill; either is null while nobody has said which utility was meant.
 */

/**
 * How far one service of a bill has got towards being allocated. Anything past `allocating` is the
 * allocation service's own account of it, read live.
 */
export const BILL_ALLOCATION_STATE = {
  /** Nobody has allocated it; the card offers the suggestion. */
  notAllocated: 'NotAllocated',
  /** The allocation service refused it. `error` says why, and it can be tried again. */
  failed: 'Failed',
  /** The allocation bill exists and its table is being worked out. */
  allocating: 'Allocating',
  /** The table is worked out and waiting to be approved. */
  awaitingApproval: 'AwaitingApproval',
  /** The bill rose sharply enough that somebody has to compare it before it can be approved. */
  needsReview: 'NeedsReview',
  /** Approved: the allocation table is the thing to show, not a suggestion. */
  allocated: 'Allocated',
  /** The table was skipped, so this service will not be charged on. */
  skipped: 'Skipped'
};

/** One service of a bill and where its allocation has got to. */

/** One service to allocate, with the values as they stand on the card. */

/** A refused delete is an answer rather than a broken request: `message` says what holds the bill. */

/** Status envelope returned by POST /bills and GET /bills/{billId}. */

/** `response` is null when nothing has been uploaded for that cycle, property and utility yet. */

/** Why the bill could not be pushed (no AppFolio vendor, no GL code, unmapped property...). */

/** Present when the same invoice already reached AppFolio, through this bill or another one. */

/** A property covered by one attempt, with the utilities and the share of the bill it carries. */

/**
 * One AP push attempt. A document is one invoice and one payable, so a trigger records exactly one
 * attempt; the extra rows a status read carries are that attempt's history.
 */

/** Push state of one bill, rolled up over its attempts. */

export class BillAutomationBills {
  constructor() {
    _defineProperty(this, "routes", billAutomationBillsRoutes);
  }
  async startBill(data, options) {
    const formData = new FormData();
    formData.append('PropertyId', data.propertyId);
    formData.append('File', data.file);
    const appendList = (key, value) => {
      if (!value) return;
      const list = Array.isArray(value) ? value : [value];
      list.filter(Boolean).forEach(item => formData.append(key, item));
    };
    appendList('UtilityNames', data.utilityNames);
    if (data.propertyAddress) formData.append('PropertyAddress', data.propertyAddress);
    appendList('RequiredUtilities', data.requiredUtilities);
    if (data.billingCycleId) formData.append('BillingCycleId', data.billingCycleId);
    if (data.utilityId) formData.append('UtilityId', data.utilityId);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.baseUrl, formData, _objectSpread({}, options.cancelToken));
    return resp.data;
  }
  async getBill(billId, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billById(billId), _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /**
   * The bill somebody uploaded for this cycle, property and utility, whatever state it is in — how
   * apply billing picks up the document it was working on without having kept its id. Resolves with
   * a null `response` when there is none, which is the ordinary answer, not a failure.
   */
  async getBillForCycle(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billForCycle, _objectSpread({
      params: {
        billingCycleId: data.billingCycleId,
        propertyId: data.propertyId,
        utilityId: data.utilityId
      }
    }, options.cancelToken));
    return resp.data;
  }

  /** Lands every file as a Pending bill; per-file outcomes come back in the response array. */
  async uploadBillsBatch(data, options) {
    var _data$propertyIds, _data$propertyAssignm;
    const formData = new FormData();
    data.files.forEach(file => formData.append('Files', file));
    ((_data$propertyIds = data.propertyIds) !== null && _data$propertyIds !== void 0 ? _data$propertyIds : []).forEach(propertyId => formData.append('PropertyIds', propertyId));
    // One entry per file, so a file with no property still has to take its place in the list.
    ((_data$propertyAssignm = data.propertyAssignments) !== null && _data$propertyAssignm !== void 0 ? _data$propertyAssignm : []).forEach(propertyIds => formData.append('PropertyAssignments', propertyIds.join(',')));
    const onUploadProgress = data.onUploadProgress;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.companyBillsBatch(data.companyId), formData, _objectSpread(_objectSpread({
      baseURL: billingDashboardBaseUrl
    }, onUploadProgress ? {
      onUploadProgress: event => {
        var _event$loaded, _event$total;
        return onUploadProgress((_event$loaded = event.loaded) !== null && _event$loaded !== void 0 ? _event$loaded : 0, (_event$total = event.total) !== null && _event$total !== void 0 ? _event$total : 0);
      }
    } : {}), options.cancelToken));
    return resp.data;
  }

  /** Where every service on the bill has got to, without changing anything. */
  async getBillAllocations(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billAllocations(data.companyId, data.billId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * A short-lived presigned URL for the bill's stored source document (LB-5724 PR A). 404 when the
   * bill has no stored document — the caller shows a "no document" state. The URL is ephemeral: open
   * it in an iframe / new tab; never fetch()/XHR it (the bill bucket has no CORS) and never cache it.
   */
  async getBillFileUrl(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillFileUrl(data.companyId, data.billId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Takes the bill as far towards allocated as it can go: creates the allocation bill for each
   * service named, then approves whatever table has finished calculating. Safe to repeat, and meant
   * to be — sending no services asks it to carry on with what is already started, which is how the
   * card follows a table from created to approved.
   */
  async allocateBill(data, options) {
    var _data$services;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.allocateBill(data.companyId, data.billId), {
      services: (_data$services = data.services) !== null && _data$services !== void 0 ? _data$services : []
    }, _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Deletes the bill for good. The service refuses a bill any of whose services has been allocated
   * and answers 409 with the reason, which is read here rather than thrown: nothing is broken, the
   * bill is simply not the caller's to delete any more.
   */
  /**
   * Approve-on-attach (LB-5766): attach a Rejected or NeedsProperty bill to a property and, in the
   * same gesture, allow its sender (or their whole domain) and save its account number — so the
   * next bill from this sender attaches itself. 409 carries the service's refusal (wrong status,
   * concurrent change) as an answer, not an error.
   */
  async attachBill(data, options) {
    var _message, _resp$data;
    const billId = data.billId,
      body = _objectWithoutProperties(data, _excluded);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.attachBill(billId), body, _objectSpread({
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? {
      isSuccessful: false,
      message: (_message = (_resp$data = resp.data) === null || _resp$data === void 0 ? void 0 : _resp$data.message) !== null && _message !== void 0 ? _message : null
    } : resp.data;
  }
  async deleteBill(data, options) {
    var _resp$data$message, _resp$data2;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).delete(this.routes.companyBill(data.companyId, data.billId), undefined, _objectSpread({
      baseURL: billingDashboardBaseUrl,
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? {
      isSuccessful: false,
      message: (_resp$data$message = (_resp$data2 = resp.data) === null || _resp$data2 === void 0 ? void 0 : _resp$data2.message) !== null && _resp$data$message !== void 0 ? _resp$data$message : null
    } : {
      isSuccessful: true,
      message: null
    };
  }
  async getCompanyBills(data, options) {
    var _data$page, _data$pageSize;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBills(data.companyId), _objectSpread({
      baseURL: billingDashboardBaseUrl,
      params: {
        page: (_data$page = data.page) !== null && _data$page !== void 0 ? _data$page : 1,
        pageSize: (_data$pageSize = data.pageSize) !== null && _data$pageSize !== void 0 ? _data$pageSize : 100
      }
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Pushes an uploaded bill to AppFolio Accounts Payable, without waiting for an allocation. Accepted (202)
   * only means the request was taken: check each attempt's `dispatched` flag, and its `blockers` /
   * `alreadyPushed` when it is false. 422 when the bill as a whole cannot be pushed.
   */
  /**
   * Both body fields stand in for something a person supplied that the document's own extraction could
   * not: `overrideTotalAmount` when they edited the Bill total by hand, `invoiceNumber` when the bill
   * carries none, or carries one the extraction was not confident about.
   *
   * Each is dropped unless it has a value, and a push with neither sends no body at all — the ordinary
   * case, and the one every caller took before these existed.
   */
  async pushBillToAppfolio(billId, body, options) {
    const supplied = Object.fromEntries(Object.entries(body !== null && body !== void 0 ? body : {}).filter(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        value = _ref2[1];
      return value !== undefined && value !== null && value !== '';
    }));
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.appfolioPush(billId), Object.keys(supplied).length ? supplied : undefined, _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /** AppFolio AP push status for a batch of uploaded bills, one entry per requested bill id. */
  async getAppfolioPushStatus(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.appfolioPushStatus, {
      billIds: data.billIds
    }, _objectSpread({}, options.cancelToken));
    return resp.data;
  }
}